import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MapPane,
  clamp01,
  computeAutoBins,
  formatBin,
  formatInt,
  getBinIndex,
  getOkrsekTail,
  getPaletteSteps,
  getPartiesFromResults,
  getResultDisplayName,
  getStatsForOkrsek,
  mixHex,
  tintToWhite,
  useElectionData,
} from "./Map";
import { APP_PAGE_BACKGROUND, BRAND_COLORS, BRAND_MAP_COLOR_PRESETS, normalizeHexColor } from "./branding";
import { ELECTION_MAP_STANDARD_V1_KEY, getElectionMapFeatureStyle } from "./mapRenderingStandard";
import { getElectionLabel, sortElectionsNewestFirst } from "./electionOrdering";
import {
  getStoredChoiceForElection,
  pickValidElectionId,
  pickValidChoice,
  readElectionSelectionContext,
  setStoredChoiceForElection,
  writeElectionSelectionContext,
} from "./electionSelectionContext";
import {
  clearStoredViewport,
  getInitialMapView,
  makeAutoFitRequestKey,
  readStoredViewport,
  writeStoredViewport,
} from "./mapViewport";

const STORAGE_KEY = "electionmap.analyticsOverview.v2";
const DEFAULT_VISIBLE_ROWS = 10;
const DEFAULT_SCALE_COLOR = BRAND_COLORS.primaryBrightBlue;
const ANALYTICS_SELECTED_BORDER_WEIGHT = 2.4;
const NO_DATA_FILL_COLOR = "#e5e7eb";
const POSITIVE_FILL_BASE = BRAND_COLORS.primaryBrightBlue;
const NEGATIVE_FILL_BASE = BRAND_COLORS.accentRed;
const MONO_FONT = 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

const OVERVIEW_COLOR_PRESETS = BRAND_MAP_COLOR_PRESETS;

function readStoredSettings() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function chooseDefaultElectionId(elections) {
  const list = Array.isArray(elections) ? elections : [];
  const preferredKv2022 = list.find(
    (item) => String(item && item.id ? item.id : "").startsWith("kv2022_") && !String(item.id).endsWith("_typ1")
  );
  if (preferredKv2022) return String(preferredKv2022.id);
  const first = list[0];
  return first && first.id ? String(first.id) : "";
}

function getPresetHex(value) {
  const normalized = normalizeHexColor(value);
  const preset = OVERVIEW_COLOR_PRESETS.find((item) => item.hex.toLowerCase() === normalized);
  return preset ? preset.hex : normalized || DEFAULT_SCALE_COLOR;
}

function getPartyResultForPrecinct(resultsByPrecinct, precinctId, selectedParty) {
  const list = resultsByPrecinct.get(String(precinctId)) || [];
  return list.find((row) => getResultDisplayName(row) === selectedParty) || null;
}

function sumResultVotes(resultList) {
  return (Array.isArray(resultList) ? resultList : []).reduce((acc, row) => {
    const votes = Number(row && row.votes);
    return Number.isFinite(votes) ? acc + votes : acc;
  }, 0);
}

function formatOverviewPct(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "–";
  return `${number.toLocaleString("cs-CZ", {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 1,
    maximumFractionDigits: 1,
  })} %`;
}

function formatOverviewBinRange(from, to) {
  return `${formatBin(from)}–${formatBin(to)} %`;
}

function shortenTooltipLabel(value, maxLength = 34) {
  const label = String(value || "").trim();
  if (!label) return "";
  if (label.length <= maxLength) return label;
  return `${label.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`;
}

function getPrecinctTooltipFields(row, selectedParty) {
  const partyName = String(selectedParty || "Vybraná strana").trim();
  return {
    title: `Okrsek ${row.precinctLabel} - ${formatInt(row.eligibleVoters)} voličů`,
    totalVotes: `Počet hlasujících - ${formatInt(row.totalVotes)}`,
    turnout: `Účast - ${formatOverviewPct(row.turnoutPct)}`,
    partyName,
    partyDisplayName: shortenTooltipLabel(partyName),
    resultValue: `${formatOverviewPct(row.partyPercentage)} (${formatInt(row.partyVotes)} hlasů)`,
  };
}

function buildPrecinctRows(districtsGeoJson, results, stats, electionId, selectedParty) {
  const features = Array.isArray(districtsGeoJson && districtsGeoJson.features) ? districtsGeoJson.features : [];
  const resultsList = Array.isArray(results) ? results : [];
  const statsList = Array.isArray(stats) ? stats : [];

  const resultsByPrecinct = new Map();
  for (const row of resultsList) {
    const precinctId = String(row && row.okrsek ? row.okrsek : "").trim();
    if (!precinctId) continue;
    resultsByPrecinct.set(precinctId, Array.isArray(row.results) ? row.results : []);
  }

  const statsByPrecinct = new Map();
  for (const row of statsList) {
    const precinctId = String(row && row.okrsek ? row.okrsek : "").trim();
    if (!precinctId) continue;
    statsByPrecinct.set(precinctId, row);
  }

  const knownPrecincts = new Map();
  const rememberPrecinct = (precinctId) => {
    const key = String(precinctId || "").trim();
    if (!key || knownPrecincts.has(key)) return;
    knownPrecincts.set(key, {
      precinctId: key,
      precinctLabel: getOkrsekTail(key),
    });
  };

  for (const feature of features) {
    rememberPrecinct(feature && feature.properties ? feature.properties.okrsek : "");
  }
  for (const row of resultsList) {
    rememberPrecinct(row && row.okrsek);
  }
  for (const row of statsList) {
    rememberPrecinct(row && row.okrsek);
  }

  return Array.from(knownPrecincts.values()).map((base) => {
    const resultList = resultsByPrecinct.get(base.precinctId) || [];
    const partyResult = getPartyResultForPrecinct(resultsByPrecinct, base.precinctId, selectedParty);
    const statsInfo = getStatsForOkrsek(statsByPrecinct, base.precinctId, electionId);

    return {
      ...base,
      resultList,
      partyVotes: partyResult && Number.isFinite(Number(partyResult.votes)) ? Number(partyResult.votes) : null,
      partyPercentage:
        partyResult && Number.isFinite(Number(partyResult.percentage)) ? Number(partyResult.percentage) : null,
      totalResultVotes: sumResultVotes(resultList),
      eligibleVoters: statsInfo && Number.isFinite(Number(statsInfo.voters)) ? Number(statsInfo.voters) : null,
      totalVotes: statsInfo && Number.isFinite(Number(statsInfo.totalVotes)) ? Number(statsInfo.totalVotes) : null,
      turnoutPct: statsInfo && Number.isFinite(Number(statsInfo.turnoutPct)) ? Number(statsInfo.turnoutPct) : null,
    };
  });
}

function compareMetricDesc(metricKey) {
  return (a, b) => {
    const av = Number(a && a[metricKey]);
    const bv = Number(b && b[metricKey]);
    const aValue = Number.isFinite(av) ? av : Number.NEGATIVE_INFINITY;
    const bValue = Number.isFinite(bv) ? bv : Number.NEGATIVE_INFINITY;
    if (bValue !== aValue) return bValue - aValue;
    return String(a && a.precinctLabel ? a.precinctLabel : "").localeCompare(
      String(b && b.precinctLabel ? b.precinctLabel : "")
    );
  };
}

function compareMetricAsc(metricKey) {
  return (a, b) => {
    const av = Number(a && a[metricKey]);
    const bv = Number(b && b[metricKey]);
    const aValue = Number.isFinite(av) ? av : Number.POSITIVE_INFINITY;
    const bValue = Number.isFinite(bv) ? bv : Number.POSITIVE_INFINITY;
    if (aValue !== bValue) return aValue - bValue;
    return String(a && a.precinctLabel ? a.precinctLabel : "").localeCompare(
      String(b && b.precinctLabel ? b.precinctLabel : "")
    );
  };
}

function getRankFillColor(baseColor, index, totalRows) {
  if (totalRows <= 1) return tintToWhite(baseColor, 0.1);
  const ratio = clamp01(index / Math.max(1, totalRows - 1));
  return tintToWhite(baseColor, 0.08 + ratio * 0.62);
}

function getAnalyticsClassColors(baseColor, classCount) {
  const count = Math.max(1, Number(classCount) || 1);
  const anchors = getPaletteSteps(baseColor);
  if (count === anchors.length) return anchors;
  if (count === 1) return [anchors[anchors.length - 1]];

  return Array.from({ length: count }, (_, index) => {
    const position = index / (count - 1);
    const scaled = position * (anchors.length - 1);
    const lowerIndex = Math.floor(scaled);
    const upperIndex = Math.min(anchors.length - 1, Math.ceil(scaled));
    if (lowerIndex === upperIndex) return anchors[lowerIndex];
    return mixHex(anchors[lowerIndex], anchors[upperIndex], scaled - lowerIndex);
  });
}

function ErrorList({ messages }) {
  if (!messages.length) return null;
  return (
    <div className="analytics-error-list" role="alert">
      {messages.map((message) => (
        <div key={message}>{message}</div>
      ))}
    </div>
  );
}

function HoverTooltip({ tooltip, row, selectedParty }) {
  if (!tooltip || !row) return null;
  const fields = getPrecinctTooltipFields(row, selectedParty);
  return (
    <div
      className="analytics-hover-tooltip"
      style={{
        left: tooltip.x,
        top: tooltip.y,
      }}
    >
      <div className="tt-title">{fields.title}</div>
      <div className="tt-row">{fields.totalVotes}</div>
      <div className="tt-row">{fields.turnout}</div>
      <div className="tt-row tt-row-strong">
        <span className="tt-party-label" title={fields.partyName}>
          {fields.partyDisplayName}
        </span>
        <span>: {fields.resultValue}</span>
      </div>
    </div>
  );
}

function KpiCard({ title, value, subtitle, accentColor }) {
  return (
    <section className="analytics-kpi-card">
      <div className="analytics-kpi-accent" style={{ background: accentColor }} aria-hidden="true" />
      <div className="analytics-kpi-title">{title}</div>
      <div className="analytics-kpi-value">{value}</div>
      <div className="analytics-kpi-subtitle">{subtitle}</div>
    </section>
  );
}

function MapLegend({ legendItems, selectedLegendClassIndex, onToggleLegendClass }) {
  if (!legendItems.length) {
    return <div className="analytics-legend-empty">Legenda není k dispozici.</div>;
  }

  return (
    <div className="analytics-legend-list">
      {legendItems.map((item) => (
        <button
          key={item.label}
          type="button"
          className={`analytics-legend-item${selectedLegendClassIndex === item.index ? " is-selected" : ""}`}
          aria-pressed={selectedLegendClassIndex === item.index}
          onClick={() => onToggleLegendClass(item.index)}
        >
          <span className="analytics-legend-dot" style={{ background: item.color }} aria-hidden="true" />
          <span className="analytics-legend-text">{item.label}</span>
        </button>
      ))}
    </div>
  );
}

function RankList({
  title,
  rows,
  metricKey,
  formatValue,
  accentColor,
  hoveredPrecinct,
  selectedPrecinctIds,
  setHoveredPrecinct,
  toggleSelectedPrecinct,
  showTooltipAtEvent,
  hideTooltip,
}) {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_ROWS);

  useEffect(() => {
    setVisibleCount(DEFAULT_VISIBLE_ROWS);
  }, [rows, title]);

  const visibleRows = rows.slice(0, visibleCount);
  const maxMetricValue = useMemo(() => {
    const values = rows
      .map((row) => Number(row && row[metricKey]))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (values.length === 0) return 1;
    return Math.max(...values);
  }, [metricKey, rows]);

  return (
    <div className="analytics-rank-list">
      <div className="analytics-rank-list-header">
        <h3>{title}</h3>
        <span>{rows.length} okrsků</span>
      </div>

      <div className="analytics-rank-rows">
        {visibleRows.length > 0 ? (
          visibleRows.map((row, index) => {
            const metricValue = Number(row && row[metricKey]);
            const normalizedWidth =
              Number.isFinite(metricValue) && maxMetricValue > 0
                ? `${Math.max(6, (metricValue / maxMetricValue) * 100)}%`
                : "6%";
            const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === String(row.precinctId);
            const isSelected = selectedPrecinctIds.has(String(row.precinctId));
            const isHighlighted = isHovered || isSelected;
            const fillColor = isHighlighted ? accentColor : getRankFillColor(accentColor, index, visibleRows.length);
            const rowTint = isHighlighted ? tintToWhite(accentColor, 0.92) : "#f8fafc";
            const rowBorder = isHighlighted ? tintToWhite(accentColor, 0.5) : "#e2e8f0";

            return (
              <button
                key={`${title}-${row.precinctId}`}
                type="button"
                className="analytics-rank-row"
                onMouseEnter={(event) => {
                  setHoveredPrecinct(row.precinctId);
                  showTooltipAtEvent("row", row.precinctId, event);
                }}
                onMouseMove={(event) => showTooltipAtEvent("row", row.precinctId, event)}
                onMouseLeave={() => {
                  setHoveredPrecinct((current) => (String(current || "") === String(row.precinctId) ? null : current));
                  hideTooltip("row");
                }}
                onFocus={(event) => {
                  setHoveredPrecinct(row.precinctId);
                  showTooltipAtEvent("row", row.precinctId, event);
                }}
                onBlur={() => {
                  setHoveredPrecinct((current) => (String(current || "") === String(row.precinctId) ? null : current));
                  hideTooltip("row");
                }}
                onClick={() => toggleSelectedPrecinct(row.precinctId)}
                style={{
                  background: rowTint,
                  borderColor: rowBorder,
                  boxShadow: isHighlighted ? "0 10px 24px rgba(15, 23, 42, 0.08)" : "none",
                }}
              >
                <span className="analytics-rank-index">{index + 1}.</span>
                <span className="analytics-rank-precinct" title={`Okrsek ${row.precinctLabel}`}>
                  {row.precinctLabel}
                </span>
                <span className="analytics-rank-bar" aria-hidden="true">
                  <span
                    className="analytics-rank-bar-fill"
                    style={{
                      width: normalizedWidth,
                      background: fillColor,
                    }}
                  />
                </span>
                <span className="analytics-rank-value">{formatValue(metricValue)}</span>
              </button>
            );
          })
        ) : (
          <div className="analytics-rank-empty">Pro zvolenou kombinaci zatím nejsou k dispozici řádky.</div>
        )}
      </div>

      {rows.length > DEFAULT_VISIBLE_ROWS ? (
        <div className="analytics-rank-actions">
          {visibleCount < rows.length ? (
            <button
              type="button"
              className="analytics-inline-button"
              onClick={() => setVisibleCount((current) => Math.min(current + DEFAULT_VISIBLE_ROWS, rows.length))}
            >
              Zobrazit dalších 10
            </button>
          ) : null}
          {visibleCount > DEFAULT_VISIBLE_ROWS ? (
            <button type="button" className="analytics-inline-button" onClick={() => setVisibleCount(DEFAULT_VISIBLE_ROWS)}>
              Sbalit
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function AnalyticsSection({ title, children }) {
  return (
    <section className="analytics-section">
      <div className="analytics-section-header">
        <h2>{title}</h2>
      </div>
      <div className="analytics-section-grid">{children}</div>
    </section>
  );
}

export default function AnalyticsOverviewPage({ areaId }) {
  const initialSettings = useMemo(() => readStoredSettings(), []);
  const [elections, setElections] = useState([]);
  const [electionId, setElectionId] = useState("");
  const [selectedParty, setSelectedParty] = useState("");
  const [hoveredPrecinct, setHoveredPrecinct] = useState(null);
  const [selectedPrecinctIds, setSelectedPrecinctIds] = useState(() => new Set());
  const [selectedLegendClassIndex, setSelectedLegendClassIndex] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [baseColor, setBaseColor] = useState(() => getPresetHex(initialSettings && initialSettings.baseColor));
  const [classCount, setClassCount] = useState(() => {
    const next = Number(initialSettings && initialSettings.classCount);
    return next === 3 || next === 5 || next === 7 ? next : 5;
  });

  const mapRef = useRef(null);
  const geoLayerRef = useRef(null);
  const layerIndexRef = useRef(new Map());
  const prevHoveredRef = useRef(null);
  const initialViewport = useMemo(() => getInitialMapView("analytics", areaId), [areaId]);
  const hasStoredInitialViewportRef = useRef(Boolean(readStoredViewport("analytics", areaId)));
  const [fitRequestKey, setFitRequestKey] = useState(() =>
    hasStoredInitialViewportRef.current ? "" : makeAutoFitRequestKey("analytics", areaId, "initial")
  );
  const prevAreaIdForViewportRef = useRef(areaId);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          baseColor,
          classCount,
        })
      );
    } catch (_) {
      // Ignore local storage failures.
    }
  }, [baseColor, classCount]);

  useEffect(() => {
    if (!areaId) {
      setElections([]);
      setElectionId("");
      return;
    }

    let cancelled = false;
    setElections([]);
    setElectionId("");

    (async () => {
      try {
        const response = await fetch(`/api/areas/${encodeURIComponent(String(areaId))}/elections`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error(`enabled elections HTTP ${response.status}`);
        const payload = await response.json();
        if (cancelled) return;

        const nextElections = sortElectionsNewestFirst(Array.isArray(payload) ? payload : []);
        const storedSelection = readElectionSelectionContext(areaId);
        const preferredElectionId = chooseDefaultElectionId(nextElections);

        setElections(nextElections);
        setElectionId((current) => {
          return pickValidElectionId(
            nextElections,
            [
              current,
              storedSelection.analyticsElectionId,
              storedSelection.lastElectionId,
              storedSelection.appElectionIdA,
              storedSelection.appElectionIdB,
            ],
            preferredElectionId
          );
        });
      } catch (error) {
        console.error("[AnalyticsOverviewPage] failed to load elections", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [areaId]);

  useEffect(() => {
    if (!areaId || !electionId) return;
    const storedSelection = readElectionSelectionContext(areaId);
    writeElectionSelectionContext(areaId, {
      analyticsElectionId: electionId,
      analyticsPartyByElection: setStoredChoiceForElection(
        storedSelection,
        "analyticsPartyByElection",
        electionId,
        selectedParty
      ),
      lastElectionId: electionId,
    });
  }, [areaId, electionId, selectedParty]);

  useEffect(() => {
    if (prevAreaIdForViewportRef.current === areaId) return;
    prevAreaIdForViewportRef.current = areaId;
    clearStoredViewport("analytics", areaId);
    setFitRequestKey(makeAutoFitRequestKey("analytics", areaId, "area"));
  }, [areaId]);

  const resetMapToArea = useCallback(() => {
    setHoveredPrecinct(null);
    setSelectedPrecinctIds(new Set());
    setSelectedLegendClassIndex(null);
    setTooltip(null);
    clearStoredViewport("analytics", areaId);
    setFitRequestKey(makeAutoFitRequestKey("analytics", areaId, "reset"));
  }, [areaId]);

  const data = useElectionData(electionId, areaId);
  const parties = useMemo(() => getPartiesFromResults(data.results), [data.results]);

  useEffect(() => {
    if (!parties.length) {
      setSelectedParty("");
      return;
    }
    if (!selectedParty || !parties.includes(selectedParty)) {
      const storedSelection = readElectionSelectionContext(areaId);
      setSelectedParty(
        pickValidChoice(
          parties,
          [getStoredChoiceForElection(storedSelection, "analyticsPartyByElection", electionId)],
          parties[0]
        )
      );
    }
  }, [areaId, electionId, parties, selectedParty]);

  const resultsByPrecinct = useMemo(() => {
    const next = new Map();
    for (const row of Array.isArray(data.results) ? data.results : []) {
      const precinctId = String(row && row.okrsek ? row.okrsek : "").trim();
      if (!precinctId) continue;
      next.set(precinctId, Array.isArray(row.results) ? row.results : []);
    }
    return next;
  }, [data.results]);

  const precinctRows = useMemo(
    () => buildPrecinctRows(data.districtsGeoJson, data.results, data.stats, electionId, selectedParty),
    [data.districtsGeoJson, data.results, data.stats, electionId, selectedParty]
  );

  const rowsByPrecinct = useMemo(() => {
    const next = new Map();
    for (const row of precinctRows) {
      next.set(String(row.precinctId), row);
    }
    return next;
  }, [precinctRows]);

  const tooltipRow = useMemo(() => {
    if (!tooltip || !tooltip.precinctId) return null;
    return rowsByPrecinct.get(String(tooltip.precinctId)) || null;
  }, [rowsByPrecinct, tooltip]);

  const valuesPct = useMemo(
    () =>
      precinctRows
        .map((row) => row.partyPercentage)
        .filter((value) => Number.isFinite(value))
        .map((value) => Number(value)),
    [precinctRows]
  );

  const activeBins = useMemo(() => computeAutoBins(valuesPct, classCount), [classCount, valuesPct]);
  const classColors = useMemo(
    () => getAnalyticsClassColors(baseColor, Math.max(1, activeBins.length - 1)),
    [activeBins.length, baseColor]
  );

  const fillColorForPct = useCallback(
    (percentage) => {
      const index = getBinIndex(percentage, activeBins);
      if (index < 0) return NO_DATA_FILL_COLOR;
      return classColors[Math.min(index, classColors.length - 1)] || classColors[classColors.length - 1] || baseColor;
    },
    [activeBins, baseColor, classColors]
  );

  const legendItems = useMemo(() => {
    const items = [];
    for (let index = 0; index < activeBins.length - 1; index += 1) {
      const from = activeBins[index];
      const to = activeBins[index + 1];
      const midpoint = from + (to - from) / 2;
      items.push({
        index,
        label: formatOverviewBinRange(from, to),
        color: fillColorForPct(midpoint),
      });
    }
    return items;
  }, [activeBins, fillColorForPct]);

  const styleFeature = useCallback(
    (feature) => {
      const precinctId = String(feature && feature.properties ? feature.properties.okrsek || "" : "");
      const partyResult = getPartyResultForPrecinct(resultsByPrecinct, precinctId, selectedParty);
      const percentage =
        partyResult && Number.isFinite(Number(partyResult.percentage)) ? Number(partyResult.percentage) : null;
      const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === precinctId;
      const isSelected = selectedPrecinctIds.has(precinctId);
      return getElectionMapFeatureStyle({
        fillColor: fillColorForPct(percentage),
        noDataFillColor: NO_DATA_FILL_COLOR,
        isHovered,
        isSelected,
        clearOnFocus: true,
        selectedBorderWeight: ANALYTICS_SELECTED_BORDER_WEIGHT,
      });
    },
    [fillColorForPct, hoveredPrecinct, resultsByPrecinct, selectedParty, selectedPrecinctIds]
  );

  const hideTooltip = useCallback((source) => {
    setTooltip((current) => {
      if (!current) return null;
      if (source && current.source !== source) return current;
      return null;
    });
  }, []);

  const showTooltipAtEvent = useCallback((source, precinctId, event) => {
    const clientX = Number(
      event && event.clientX != null ? event.clientX : event && event.originalEvent ? event.originalEvent.clientX : null
    );
    const clientY = Number(
      event && event.clientY != null ? event.clientY : event && event.originalEvent ? event.originalEvent.clientY : null
    );
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;

    setTooltip({
      source,
      precinctId: String(precinctId || ""),
      x: Math.max(12, Math.min(clientX + 18, viewportWidth - 320)),
      y: Math.max(12, Math.min(clientY + 18, viewportHeight - 130)),
    });
  }, []);

  const toggleSelectedPrecinct = useCallback((precinctId) => {
    const nextPrecinct = String(precinctId || "");
    if (!nextPrecinct) return;
    setSelectedLegendClassIndex(null);
    setSelectedPrecinctIds((current) => {
      const next = new Set(current);
      if (next.has(nextPrecinct)) next.delete(nextPrecinct);
      else next.add(nextPrecinct);
      return next;
    });
  }, []);

  const toggleLegendClass = useCallback(
    (classIndex) => {
      const nextClassIndex = Number(classIndex);
      if (!Number.isInteger(nextClassIndex)) return;

      if (selectedLegendClassIndex === nextClassIndex) {
        setSelectedLegendClassIndex(null);
        setSelectedPrecinctIds(new Set());
        return;
      }

      const next = new Set();
      for (const row of precinctRows) {
        if (getBinIndex(row.partyPercentage, activeBins) === nextClassIndex) {
          next.add(String(row.precinctId));
        }
      }
      setSelectedLegendClassIndex(nextClassIndex);
      setSelectedPrecinctIds(next);
    },
    [activeBins, precinctRows, selectedLegendClassIndex]
  );

  const mapKey = `analytics-overview-${areaId || "none"}-${electionId || "none"}`;
  const geoJsonKey = `${electionId || "none"}-${selectedParty || "none"}-${baseColor}-${classCount}`;

  useEffect(() => {
    layerIndexRef.current = new Map();
    prevHoveredRef.current = null;
    setHoveredPrecinct(null);
    setSelectedPrecinctIds(new Set());
    setSelectedLegendClassIndex(null);
    setTooltip(null);
  }, [electionId, mapKey]);

  useEffect(() => {
    setSelectedPrecinctIds((current) => {
      const next = new Set();
      current.forEach((precinctId) => {
        if (rowsByPrecinct.has(String(precinctId))) next.add(String(precinctId));
      });
      return next.size === current.size ? current : next;
    });
    if (hoveredPrecinct != null && !rowsByPrecinct.has(String(hoveredPrecinct))) {
      setHoveredPrecinct(null);
    }
  }, [hoveredPrecinct, rowsByPrecinct]);

  useEffect(() => {
    for (const layer of layerIndexRef.current.values()) {
      if (!layer || typeof layer.setStyle !== "function") continue;
      try {
        layer.setStyle(styleFeature(layer.feature));
      } catch (_) {
        // Ignore stale layer references.
      }
    }

    if (!hoveredPrecinct) return;
    const hoveredLayer = layerIndexRef.current.get(String(hoveredPrecinct));
    if (!hoveredLayer || typeof hoveredLayer.setStyle !== "function") return;
    try {
      hoveredLayer.setStyle(styleFeature(hoveredLayer.feature));
      if (typeof hoveredLayer.bringToFront === "function") hoveredLayer.bringToFront();
    } catch (_) {
      // Ignore stale layer references.
    }
  }, [hoveredPrecinct, styleFeature]);

  useEffect(() => {
    const previousPrecinct = prevHoveredRef.current ? String(prevHoveredRef.current) : null;
    const nextPrecinct = hoveredPrecinct ? String(hoveredPrecinct) : null;

    const resetLayer = (precinctId) => {
      if (!precinctId) return;
      const layer = layerIndexRef.current.get(String(precinctId));
      if (!layer || typeof layer.setStyle !== "function") return;
      try {
        layer.setStyle(styleFeature(layer.feature));
      } catch (_) {
        // Ignore stale layer references.
      }
    };

    if (previousPrecinct && previousPrecinct !== nextPrecinct) {
      resetLayer(previousPrecinct);
    }

    if (nextPrecinct) {
      const layer = layerIndexRef.current.get(nextPrecinct);
      if (layer && typeof layer.setStyle === "function") {
        try {
          layer.setStyle(styleFeature(layer.feature));
          if (typeof layer.bringToFront === "function") layer.bringToFront();
        } catch (_) {
          // Ignore stale layer references.
        }
      }
    }

    prevHoveredRef.current = nextPrecinct;
  }, [hoveredPrecinct, styleFeature]);

  const onEachFeature = useCallback(
    (feature, layer) => {
      const precinctId = String(feature && feature.properties ? feature.properties.okrsek || "" : "");
      if (!precinctId) return;

      layerIndexRef.current.set(precinctId, layer);
      layer.on({
        mouseover: (event) => {
          setHoveredPrecinct(precinctId);
          showTooltipAtEvent("map", precinctId, event);
        },
        mousemove: (event) => {
          showTooltipAtEvent("map", precinctId, event);
        },
        mouseout: () => {
          setHoveredPrecinct((current) => (String(current || "") === String(precinctId) ? null : current));
          hideTooltip("map");
        },
        click: () => {
          toggleSelectedPrecinct(precinctId);
        },
      });
    },
    [hideTooltip, showTooltipAtEvent, toggleSelectedPrecinct]
  );

  const strongestPrecincts = useMemo(
    () => precinctRows.filter((row) => Number.isFinite(row.partyPercentage)).slice().sort(compareMetricDesc("partyPercentage")),
    [precinctRows]
  );
  const weakestPrecincts = useMemo(
    () => precinctRows.filter((row) => Number.isFinite(row.partyPercentage)).slice().sort(compareMetricAsc("partyPercentage")),
    [precinctRows]
  );
  const largestPrecincts = useMemo(
    () => precinctRows.filter((row) => Number.isFinite(row.eligibleVoters)).slice().sort(compareMetricDesc("eligibleVoters")),
    [precinctRows]
  );
  const smallestPrecincts = useMemo(
    () => precinctRows.filter((row) => Number.isFinite(row.eligibleVoters)).slice().sort(compareMetricAsc("eligibleVoters")),
    [precinctRows]
  );
  const highestTurnoutPrecincts = useMemo(
    () => precinctRows.filter((row) => Number.isFinite(row.turnoutPct)).slice().sort(compareMetricDesc("turnoutPct")),
    [precinctRows]
  );
  const lowestTurnoutPrecincts = useMemo(
    () => precinctRows.filter((row) => Number.isFinite(row.turnoutPct)).slice().sort(compareMetricAsc("turnoutPct")),
    [precinctRows]
  );

  const selectedPartyOverallPct = useMemo(() => {
    let partyVotes = 0;
    let totalVotes = 0;
    for (const row of precinctRows) {
      if (Number.isFinite(row.partyVotes)) partyVotes += Number(row.partyVotes);
      if (Number.isFinite(row.totalResultVotes) && row.totalResultVotes > 0) totalVotes += Number(row.totalResultVotes);
    }
    if (totalVotes <= 0) return null;
    return (partyVotes / totalVotes) * 100;
  }, [precinctRows]);

  const totalEligibleVoters = useMemo(
    () =>
      precinctRows.reduce((sum, row) => (Number.isFinite(row.eligibleVoters) ? sum + Number(row.eligibleVoters) : sum), 0),
    [precinctRows]
  );

  const overallTurnoutPct = useMemo(() => {
    let voters = 0;
    let totalVotes = 0;
    for (const row of precinctRows) {
      if (Number.isFinite(row.eligibleVoters)) voters += Number(row.eligibleVoters);
      if (Number.isFinite(row.totalVotes)) totalVotes += Number(row.totalVotes);
    }
    if (voters <= 0) return null;
    return (totalVotes / voters) * 100;
  }, [precinctRows]);

  const uniqueErrors = useMemo(
    () => Array.from(new Set([data.districtsError, data.resultsError, data.statsError].filter(Boolean))),
    [data.districtsError, data.resultsError, data.statsError]
  );

  const isLoading = data.isLoadingDistricts || data.isLoadingResults || data.isLoadingStats;

  return (
    <>
      <style>{`
        .analytics-overview {
          width: 100%;
          padding: 10px 0 34px;
          display: grid;
          gap: var(--app-shell-major-gap, 12px);
          color: #0f172a;
          background: ${APP_PAGE_BACKGROUND};
          --analytics-map-height: clamp(320px, calc(var(--app-shell-available-height, 100vh) - 286px), 540px);
        }
        .analytics-filter-bar {
          display: flex;
          align-items: center;
          gap: 10px 28px;
          flex-wrap: wrap;
          padding: 10px 16px;
          border: 1px solid #d7e1eb;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.96);
          backdrop-filter: blur(10px);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
        }
        .analytics-filter-group {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .analytics-filter-group span {
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #475569;
          white-space: nowrap;
        }
        .analytics-filter-group-wide {
          flex: 1.35 1 420px;
          min-width: min(420px, 100%);
        }
        .analytics-filter-group-compact {
          flex: 1 1 320px;
          min-width: min(320px, 100%);
        }
        .analytics-filter-select {
          width: 100%;
          min-width: 0;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          min-height: 32px;
          padding: 6px 9px;
          background: white;
          color: #0f172a;
          font-size: 0.88rem;
          font-weight: 600;
          box-sizing: border-box;
        }
        .analytics-content-grid {
          display: grid;
          grid-template-columns: minmax(0, 42fr) minmax(0, 58fr);
          gap: var(--app-shell-major-column-gap, 18px);
          align-items: start;
        }
        .analytics-left-column {
          min-width: 0;
          align-self: start;
          position: sticky;
          top: calc(var(--app-shell-topbar-offset, 0px) + 10px);
        }
        .analytics-left-sticky {
          display: grid;
          gap: var(--app-shell-major-gap, 12px);
        }
        .analytics-kpi-row {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        .analytics-kpi-card {
          position: relative;
          overflow: hidden;
          min-height: 144px;
          padding: 18px 18px 16px;
          border: 1px solid #dbe4ee;
          border-radius: 22px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.05);
          display: grid;
          gap: 8px;
        }
        .analytics-kpi-accent {
          position: absolute;
          inset: 0 auto 0 0;
          width: 5px;
          border-radius: 22px 0 0 22px;
          opacity: 0.95;
        }
        .analytics-kpi-title {
          padding-left: 4px;
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #475569;
        }
        .analytics-kpi-value {
          padding-left: 4px;
          font-size: clamp(1.8rem, 3vw, 2.45rem);
          line-height: 1;
          font-weight: 900;
          letter-spacing: -0.04em;
          color: #0f172a;
        }
        .analytics-kpi-subtitle {
          padding-left: 4px;
          font-size: 0.9rem;
          line-height: 1.45;
          color: #475569;
        }
        .analytics-map-card {
          border: 1px solid #d5e0eb;
          border-radius: 12px;
          background: #ffffff;
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.06);
          padding: 0;
          overflow: hidden;
        }
        .analytics-map-shell {
          border: 0;
          border-radius: 0;
          background: transparent;
          padding: 0;
          overflow: hidden;
        }
        .analytics-map-controls {
          display: grid;
          grid-template-columns: minmax(170px, 0.95fr) auto minmax(120px, 0.65fr) minmax(0, 1.55fr);
          gap: 14px 16px;
          align-items: start;
          padding: 16px 18px;
          border: 1px solid #dbe4ee;
          border-radius: 22px;
          background: white;
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.04);
        }
        .analytics-control-stack {
          display: grid;
          gap: 8px;
          min-width: 0;
        }
        .analytics-control-label {
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #475569;
        }
        .analytics-color-select-row {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .analytics-color-preview {
          width: 28px;
          height: 28px;
          flex: 0 0 auto;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.18);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.28);
        }
        .analytics-compact-select {
          min-width: 0;
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          min-height: 34px;
          padding: 7px 10px;
          background: white;
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 600;
          box-sizing: border-box;
        }
        .analytics-legend-block {
          display: grid;
          gap: 8px;
          min-width: 0;
        }
        .analytics-legend-list {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 8px;
          align-items: flex-start;
        }
        .analytics-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          width: auto;
          min-height: 28px;
          padding: 4px 8px;
          border: 1px solid transparent;
          border-radius: 999px;
          background: transparent;
          color: inherit;
          cursor: pointer;
          text-align: left;
          font: inherit;
        }
        .analytics-legend-item:hover,
        .analytics-legend-item.is-selected {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        .analytics-legend-item.is-selected {
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
        }
        .analytics-legend-dot {
          width: 11px;
          height: 11px;
          border-radius: 999px;
          border: 1px solid #0f172a;
          flex: 0 0 auto;
        }
        .analytics-legend-text {
          font-size: 0.82rem;
          line-height: 1.3;
          color: #334155;
          white-space: nowrap;
        }
        .analytics-legend-empty {
          font-size: 0.84rem;
          color: #64748b;
        }
        .analytics-error-list {
          border: 1px solid #fecaca;
          border-radius: 16px;
          background: #fff1f2;
          color: #7f1d1d;
          padding: 12px 14px;
          display: grid;
          gap: 6px;
          font-size: 0.84rem;
        }
        .analytics-status {
          font-size: 0.84rem;
          color: #475569;
          padding: 0 2px;
        }
        .analytics-right-column {
          display: grid;
          gap: var(--app-shell-major-gap, 12px);
          min-width: 0;
        }
        .analytics-section {
          border: 1px solid #d9e2ec;
          border-radius: 26px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          box-shadow: 0 18px 36px rgba(15, 23, 42, 0.05);
          padding: 20px;
          display: grid;
          gap: 16px;
        }
        .analytics-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding-bottom: 4px;
          border-bottom: 1px solid rgba(203, 213, 225, 0.85);
        }
        .analytics-section-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 900;
          letter-spacing: -0.03em;
        }
        .analytics-section-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
          min-width: 0;
          align-items: start;
        }
        .analytics-rank-list {
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: white;
          padding: 16px;
          display: grid;
          gap: 12px;
          min-width: 0;
          align-self: start;
        }
        .analytics-rank-list-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 10px;
        }
        .analytics-rank-list-header h3 {
          margin: 0;
          font-size: 1rem;
          line-height: 1.3;
          font-weight: 800;
        }
        .analytics-rank-list-header span {
          font-size: 0.78rem;
          color: #64748b;
          white-space: nowrap;
        }
        .analytics-rank-rows {
          display: grid;
          gap: 8px;
        }
        .analytics-rank-row {
          display: grid;
          grid-template-columns: 36px minmax(56px, auto) minmax(0, 1fr) auto;
          gap: 8px;
          align-items: center;
          width: 100%;
          padding: 10px 11px;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          cursor: pointer;
          text-align: left;
          transition: box-shadow 120ms ease-out, border-color 120ms ease-out, background-color 120ms ease-out;
        }
        .analytics-rank-row:focus-visible,
        .analytics-inline-button:focus-visible,
        .analytics-legend-item:focus-visible,
        .analytics-filter-select:focus-visible,
        .analytics-compact-select:focus-visible {
          outline: 2px solid #2563eb;
          outline-offset: 2px;
        }
        .analytics-rank-index {
          font-weight: 800;
          font-size: 0.82rem;
          color: #334155;
        }
        .analytics-rank-precinct {
          font-family: ${MONO_FONT};
          font-weight: 700;
          font-size: 0.82rem;
          color: #0f172a;
          white-space: nowrap;
        }
        .analytics-rank-bar {
          height: 12px;
          border-radius: 999px;
          background: linear-gradient(90deg, #edf2f7 0%, #f8fafc 100%);
          overflow: hidden;
          min-width: 0;
        }
        .analytics-rank-bar-fill {
          display: block;
          height: 100%;
          border-radius: 999px;
          transition: width 120ms ease-out, background-color 120ms ease-out;
        }
        .analytics-rank-value {
          font-size: 0.84rem;
          font-weight: 800;
          color: #0f172a;
          white-space: nowrap;
        }
        .analytics-rank-empty {
          padding: 14px 12px;
          border-radius: 12px;
          background: #f8fafc;
          color: #64748b;
          font-size: 0.88rem;
        }
        .analytics-rank-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: flex-start;
        }
        .analytics-inline-button {
          flex: 0 0 auto;
          width: auto;
          min-height: 0;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 7px 11px;
          background: white;
          cursor: pointer;
          font-size: 0.82rem;
          font-weight: 700;
          color: #0f172a;
        }
        .analytics-hover-tooltip {
          position: fixed;
          z-index: 1300;
          pointer-events: none;
          max-width: 300px;
          background: white;
          border: 1px solid #dbe3ef;
          border-radius: 14px;
          padding: 10px 12px;
          box-shadow: 0 18px 36px rgba(15, 23, 42, 0.16);
          font-size: 12px;
          color: #111827;
        }
        .analytics-hover-tooltip .tt-title {
          font-weight: 800;
          margin-bottom: 6px;
        }
        .analytics-hover-tooltip .tt-row {
          line-height: 1.45;
        }
        .analytics-hover-tooltip .tt-row-strong {
          display: flex;
          align-items: baseline;
          gap: 0;
          font-weight: 800;
          margin-top: 4px;
        }
        .analytics-hover-tooltip .tt-party-label {
          display: inline-block;
          max-width: 156px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: bottom;
        }
        @media (max-width: 1320px) {
          .analytics-content-grid {
            grid-template-columns: 1fr;
          }
          .analytics-left-column {
            position: static;
          }
        }
        @media (max-width: 960px) {
          .analytics-kpi-row,
          .analytics-section-grid {
            grid-template-columns: 1fr;
          }
          .analytics-map-controls {
            grid-template-columns: 1fr 1fr;
          }
          .analytics-legend-block {
            grid-column: 1 / -1;
          }
        }
        @media (max-width: 780px) {
          .analytics-filter-bar {
            gap: 14px;
            padding: 16px;
          }
          .analytics-filter-group {
            width: 100%;
            gap: 12px;
          }
          .analytics-filter-group span {
            min-width: 72px;
          }
          .analytics-kpi-row,
          .analytics-map-controls,
          .analytics-section-grid {
            grid-template-columns: 1fr;
          }
          .analytics-rank-row {
            grid-template-columns: 32px minmax(52px, auto) minmax(0, 1fr) auto;
          }
        }
      `}</style>

      <div className="analytics-overview">
        <section className="analytics-filter-bar" aria-label="Filtry přehledu">
          <label className="analytics-filter-group analytics-filter-group-wide">
            <span>Volby</span>
            <select className="analytics-filter-select" value={electionId} onChange={(event) => setElectionId(event.target.value)}>
              {elections.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {getElectionLabel(item)}
                </option>
              ))}
            </select>
          </label>

          <label className="analytics-filter-group analytics-filter-group-compact">
            <span>Strana</span>
            <select
              className="analytics-filter-select"
              value={selectedParty}
              onChange={(event) => setSelectedParty(event.target.value)}
            >
              {parties.map((party) => (
                <option key={party} value={party}>
                  {party}
                </option>
              ))}
            </select>
          </label>
        </section>

        <div className="analytics-content-grid">
          <aside className="analytics-left-column">
            <div className="analytics-left-sticky">
              <div className="analytics-kpi-row">
                <KpiCard
                  title="Celkový volební výsledek"
                  value={formatOverviewPct(selectedPartyOverallPct)}
                  subtitle={selectedParty || "Vyber stranu"}
                  accentColor={BRAND_COLORS.accentRed}
                />
                <KpiCard
                  title="Počet oprávněných voličů"
                  value={formatInt(totalEligibleVoters)}
                  subtitle="Součet oprávněných voličů v daných volbách."
                  accentColor={BRAND_COLORS.primaryBrightBlue}
                />
                <KpiCard
                  title="Celková účast"
                  value={formatOverviewPct(overallTurnoutPct)}
                  subtitle="Podíl hlasujících vůči oprávněným voličům."
                  accentColor={BRAND_COLORS.primaryBrightBlue}
                />
              </div>

              <section className="analytics-map-card">
                <div className="analytics-map-shell">
                  <MapPane
                    mapKey={mapKey}
                    geoJsonKey={geoJsonKey}
                    districtsGeoJson={data.districtsGeoJson}
                    styleFeature={styleFeature}
                    onEachFeature={onEachFeature}
                    mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
                    height="var(--analytics-map-height)"
                    scrollWheelZoom={false}
                    initialView={initialViewport}
                    fitGeoJsonBounds
                    fitBoundsRequestKey={fitRequestKey}
                    fitBoundsPadding={[22, 22]}
                    fitBoundsMaxZoom={14}
                    onMapReady={(map) => {
                      mapRef.current = map;
                    }}
                    onViewChange={(center, zoom) => {
                      writeStoredViewport("analytics", areaId, { center, zoom });
                    }}
                    onGeoJsonReady={(layer) => {
                      geoLayerRef.current = layer;
                    }}
                    onMouseLeave={() => {
                      setHoveredPrecinct(null);
                      hideTooltip("map");
                    }}
                    onInteractionStart={() => {
                      setHoveredPrecinct(null);
                      hideTooltip("map");
                    }}
                    onResetView={resetMapToArea}
                  />
                </div>
              </section>

              <section className="analytics-map-controls">
                <label className="analytics-control-stack">
                  <span className="analytics-control-label">Barevná škála</span>
                  <span className="analytics-color-select-row">
                    <span className="analytics-color-preview" style={{ background: baseColor }} aria-hidden="true" />
                    <select className="analytics-compact-select" value={baseColor} onChange={(event) => setBaseColor(event.target.value)}>
                      {OVERVIEW_COLOR_PRESETS.map((item) => (
                        <option key={item.hex} value={item.hex}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                <label className="analytics-control-stack">
                  <span className="analytics-control-label">Třídy</span>
                  <select
                    className="analytics-compact-select"
                    value={classCount}
                    onChange={(event) => setClassCount(Number(event.target.value))}
                  >
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                    <option value={7}>7</option>
                  </select>
                </label>

                <div className="analytics-legend-block">
                  <span className="analytics-control-label">Legenda</span>
                  <MapLegend
                    legendItems={legendItems}
                    selectedLegendClassIndex={selectedLegendClassIndex}
                    onToggleLegendClass={toggleLegendClass}
                  />
                </div>
              </section>

              <ErrorList messages={uniqueErrors} />

              {isLoading ? (
                <div className="analytics-status">
                  Načítám…
                  {data.isLoadingDistricts ? " okrsky" : ""}
                  {data.isLoadingResults ? " výsledky" : ""}
                  {data.isLoadingStats ? " statistiky" : ""}
                </div>
              ) : null}
            </div>
          </aside>

          <section className="analytics-right-column">
            <AnalyticsSection title="Výsledky">
              <RankList
                title="Nejsilnější výsledky"
                metricKey="partyPercentage"
                rows={strongestPrecincts}
                formatValue={formatOverviewPct}
                accentColor={POSITIVE_FILL_BASE}
                hoveredPrecinct={hoveredPrecinct}
                selectedPrecinctIds={selectedPrecinctIds}
                setHoveredPrecinct={setHoveredPrecinct}
                toggleSelectedPrecinct={toggleSelectedPrecinct}
                showTooltipAtEvent={showTooltipAtEvent}
                hideTooltip={hideTooltip}
              />
              <RankList
                title="Nejslabší výsledky"
                metricKey="partyPercentage"
                rows={weakestPrecincts}
                formatValue={formatOverviewPct}
                accentColor={NEGATIVE_FILL_BASE}
                hoveredPrecinct={hoveredPrecinct}
                selectedPrecinctIds={selectedPrecinctIds}
                setHoveredPrecinct={setHoveredPrecinct}
                toggleSelectedPrecinct={toggleSelectedPrecinct}
                showTooltipAtEvent={showTooltipAtEvent}
                hideTooltip={hideTooltip}
              />
            </AnalyticsSection>

            <AnalyticsSection title="Velikost okrsků">
              <RankList
                title="Největší okrsky (podle počtu voličů)"
                metricKey="eligibleVoters"
                rows={largestPrecincts}
                formatValue={formatInt}
                accentColor={POSITIVE_FILL_BASE}
                hoveredPrecinct={hoveredPrecinct}
                selectedPrecinctIds={selectedPrecinctIds}
                setHoveredPrecinct={setHoveredPrecinct}
                toggleSelectedPrecinct={toggleSelectedPrecinct}
                showTooltipAtEvent={showTooltipAtEvent}
                hideTooltip={hideTooltip}
              />
              <RankList
                title="Nejmenší okrsky (podle počtu voličů)"
                metricKey="eligibleVoters"
                rows={smallestPrecincts}
                formatValue={formatInt}
                accentColor={NEGATIVE_FILL_BASE}
                hoveredPrecinct={hoveredPrecinct}
                selectedPrecinctIds={selectedPrecinctIds}
                setHoveredPrecinct={setHoveredPrecinct}
                toggleSelectedPrecinct={toggleSelectedPrecinct}
                showTooltipAtEvent={showTooltipAtEvent}
                hideTooltip={hideTooltip}
              />
            </AnalyticsSection>

            <AnalyticsSection title="Účast">
              <RankList
                title="Nejvyšší účast"
                metricKey="turnoutPct"
                rows={highestTurnoutPrecincts}
                formatValue={formatOverviewPct}
                accentColor={POSITIVE_FILL_BASE}
                hoveredPrecinct={hoveredPrecinct}
                selectedPrecinctIds={selectedPrecinctIds}
                setHoveredPrecinct={setHoveredPrecinct}
                toggleSelectedPrecinct={toggleSelectedPrecinct}
                showTooltipAtEvent={showTooltipAtEvent}
                hideTooltip={hideTooltip}
              />
              <RankList
                title="Nejnižší účast"
                metricKey="turnoutPct"
                rows={lowestTurnoutPrecincts}
                formatValue={formatOverviewPct}
                accentColor={NEGATIVE_FILL_BASE}
                hoveredPrecinct={hoveredPrecinct}
                selectedPrecinctIds={selectedPrecinctIds}
                setHoveredPrecinct={setHoveredPrecinct}
                toggleSelectedPrecinct={toggleSelectedPrecinct}
                showTooltipAtEvent={showTooltipAtEvent}
                hideTooltip={hideTooltip}
              />
            </AnalyticsSection>
          </section>
        </div>
      </div>

      <HoverTooltip tooltip={tooltip} row={tooltipRow} selectedParty={selectedParty} />
    </>
  );
}
